/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import Dropdown from '@mui/joy/Dropdown';

import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { IClient } from '../interface/IClient';
import CustomTable, { IColumn } from './CustomTable';
import dayjs from 'dayjs';

function RowMenu({ onEdit, onDelete }: any) {
  return (
    <Dropdown>
      <MenuButton slots={{ root: IconButton }} slotProps={{ root: { variant: 'plain', color: 'neutral', size: 'sm' } }}>
        <MoreHorizRoundedIcon />
      </MenuButton>
      <Menu size='sm' sx={{ minWidth: 140 }}>
        <MenuItem onClick={onEdit}>Edit</MenuItem>
        <Divider />
        <MenuItem color='danger' onClick={onDelete}>
          Delete
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}

type Props = {
  clients: IClient[];
  onEdit: (clientId: number) => void;
  onDelete: (clientId: number) => void;
};

export default function ClientTable({ clients, onEdit, onDelete }: Props) {
  const columns: IColumn<IClient>[] = [
    {
      id: 'id',
      disablePadding: false,
      label: 'ID',
      numeric: false,
      dataIndex: 'clientId',
      width: 50,
    },
    {
      id: 'company',
      disablePadding: false,
      label: 'Company Name',
      numeric: false,
      dataIndex: 'company',
      width: 120,
      sort: true,
    },
    {
      id: 'client',
      disablePadding: false,
      label: 'Client',
      dataIndex: 'firstname',
      numeric: false,
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Avatar size='sm'>{row.firstname.charAt(0)}</Avatar>
          <div>
            <Typography level='body-xs'>
              {row.firstname} {row.lastname}
            </Typography>
            <Typography level='body-xs'>{row.email}</Typography>
          </div>
        </Box>
      ),
      width: 240,
    },
    {
      id: 'department',
      disablePadding: false,
      label: 'Department',
      numeric: false,
      dataIndex: 'department',
      width: 140,
    },
    {
      id: 'address',
      disablePadding: false,
      label: 'Address',
      numeric: false,
      dataIndex: 'street',
      render: (row) => (
        <>
          <Typography level='body-xs'>{row.street}</Typography>
          <Typography level='body-xs'>
            {row.zip} {row.city}
          </Typography>
        </>
      ),
      width: 140,
    },
    {
      id: 'created',
      disablePadding: false,
      label: 'Created',
      numeric: false,
      dataIndex: 'createdAt',
      render: (row) => (
        <>
          <Typography level='body-xs'>{row.createdAt ? dayjs(row.createdAt).format('DD.MM.YYYY - HH:mm') : ''}</Typography>
          <Typography level='body-xs'>{row.createdBy || ''}</Typography>
        </>
      ),
      width: 160,
    },
    {
      id: 'updated',
      disablePadding: false,
      label: 'Updated',
      numeric: false,
      dataIndex: 'updatedAt',
      render: (row) => (
        <>
          <Typography level='body-xs'>{row.updatedBy ? dayjs(row.updatedAt).format('DD.MM.YYYY - HH:mm') : ''}</Typography>
          <Typography level='body-xs'>{row.updatedBy || ''}</Typography>
        </>
      ),
      width: 160,
    },
    {
      id: 'actions',
      disablePadding: false,
      label: '',
      numeric: false,
      dataIndex: 'clientId',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <RowMenu onEdit={() => onEdit(row.clientId)} onDelete={() => onDelete(row.clientId)} />
        </Box>
      ),
      width: 50,
    },
  ];

  return (
    <React.Fragment>
      <CustomTable data={clients} idKey='clientId' columns={columns} />
    </React.Fragment>
  );
}
